import React from 'react';
import Skeleton from './Skeleton';
import './SkeletonVariants.scss';

// ============ TABLE SKELETON ============
export const TableSkeleton = ({ rows = 6, columns = 5 }) => (
    <div className="sk-table">
        <div className="sk-table__header">
            {Array.from({ length: columns }, (_, i) => (
                <Skeleton key={i} height="38px" />
            ))}
        </div>
        <div className="sk-table__body">
            {Array.from({ length: rows }, (_, rowIdx) => (
                <div key={rowIdx} className="sk-table__row">
                    {Array.from({ length: columns }, (_, colIdx) => (
                        <Skeleton
                            key={colIdx}
                            height="32px"
                            width={colIdx === 0 ? '85%' : '65%'}
                        />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

// ============ SCATTER PLOT SKELETON ============
export const ScatterPlotSkeleton = () => {
    const dots = Array.from({ length: 25 }).map((_, i) => ({
        id: i,
        left: `${8 + ((i * 13) % 82)}%`,
        top: `${8 + ((i * 29) % 82)}%`,
        size: `${8 + (i % 8)}px`,
    }));

    return (
        <div className="sk-scatter">
            <div className="sk-scatter__container">
                <div className="sk-scatter__y-axis">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={`y-axis-${i}`} width="28px" height="10px" />
                    ))}
                </div>

                <div className="sk-scatter__plot" style={{ position: 'relative' }}>
                    {dots.map((dot) => (
                        <Skeleton
                            key={`dot-${dot.id}`}
                            variant="circular"
                            width={dot.size}
                            height={dot.size}
                            className="sk-scatter__dot"
                            style={{ position: 'absolute', left: dot.left, top: dot.top }}
                        />
                    ))}
                </div>
            </div>

            <div className="sk-scatter__x-axis">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={`x-axis-${i}`} width="36px" height="10px" />
                ))}
            </div>

            <div className="sk-scatter__legend">
                <Skeleton width="80px" height="12px" style={{ marginBottom: '10px' }} />
                <div className="sk-scatter__legend-items">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={`legend-item-${i}`} className="sk-scatter__legend-item">
                            <Skeleton variant="circular" width="10px" height="10px" />
                            <Skeleton width="55px" height="10px" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============ HEATMAP SKELETON ============
export const HeatmapSkeleton = ({ rows = 5, columns = 29 }) => {
    const getOpacity = (rowIdx, colIdx) => {
        const base = 0.4;
        const variation = ((rowIdx * 3 + colIdx * 7) % 7) * 0.085;
        return base + variation;
    };

    return (
        <div className="sk-heatmap">
            <div className="sk-heatmap__y-axis">
                {Array.from({ length: rows }).map((_, i) => (
                    <Skeleton key={`y-axis-${i}`} width="70px" height="24px" />
                ))}
            </div>

            <div className="sk-heatmap__main">
                <div className="sk-heatmap__grid">
                    {Array.from({ length: rows }).map((_, rowIdx) => (
                        <div key={`row-${rowIdx}`} className="sk-heatmap__row">
                            {Array.from({ length: columns }).map((_, colIdx) => (
                                <Skeleton
                                    key={`cell-${rowIdx}-${colIdx}`}
                                    width="36px"
                                    height="28px"
                                    style={{ opacity: getOpacity(rowIdx, colIdx) }}
                                />
                            ))}
                        </div>
                    ))}
                </div>

                <div className="sk-heatmap__x-axis">
                    {Array.from({ length: columns }).map((_, i) => (
                        <Skeleton key={`x-axis-${i}`} width="32px" height="10px" />
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============ TREND LINE SKELETON ============
export const TrendLineSkeleton = () => (
    <div className="sk-trendline">
        <div className="sk-trendline__container">
            <div className="sk-trendline__y-axis">
                {Array.from({ length: 5 }, (_, i) => (
                    <Skeleton key={i} width="28px" height="10px" />
                ))}
            </div>

            <div className="sk-trendline__chart">
                <svg viewBox="0 0 400 180" preserveAspectRatio="none">
                    <path
                        className="sk-trendline__path"
                        d="M0,140 Q60,100 120,90 T240,70 T360,50 L400,60"
                    />
                    <path
                        className="sk-trendline__path sk-trendline__path--alt"
                        d="M0,160 Q60,140 120,130 T240,100 T360,110 L400,100"
                    />
                </svg>
            </div>
        </div>

        <div className="sk-trendline__x-axis">
            {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} width="44px" height="10px" />
            ))}
        </div>

        <div className="sk-trendline__legend">
            {Array.from({ length: 2 }, (_, i) => (
                <div key={i} className="sk-trendline__legend-item">
                    <Skeleton width="24px" height="3px" />
                    <Skeleton width="60px" height="10px" />
                </div>
            ))}
        </div>
    </div>
);

// ============ PAGE/DASHBOARD SKELETON ============
export const DashboardSkeleton = () => (
    <div className="sk-dashboard">
        <div className="sk-dashboard__header">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="sk-dashboard__dropdown">
                    <Skeleton width="80px" height="16px" style={{ marginBottom: 8 }} />
                    <Skeleton width="140px" height="38px" />
                </div>
            ))}
        </div>

        <div className="sk-dashboard__content">
            <div className="sk-dashboard__widget sk-dashboard__widget--half">
                <Skeleton width="120px" height="20px" style={{ marginBottom: 16 }} />
                <ScatterPlotSkeleton />
            </div>

            <div className="sk-dashboard__widget sk-dashboard__widget--half">
                <Skeleton width="100px" height="20px" style={{ marginBottom: 16 }} />
                <TableSkeleton />
            </div>
        </div>
    </div>
);

// ============ INSTITUTE NAME SKELETON ============
export const InstituteNameSkeleton = () => (
    <Skeleton
        width={80}
        height={16}
        variant="text"
        animation="shimmer"
        className="skeleton--dark"
    />
);
